if (!customElements.get('quick-add-modal')) {
  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');

        this.addEventListener('product-info:loaded', ({ target }) => {
          target.addPreProcessCallback(this.preprocessHTML.bind(this));
        });

        this.observePrintessNavigation();
      }

      hide(preventFocus = false) {
        const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (cartNotification) cartNotification.setActiveElement(this.openedBy);
        this.cleanupPrintess();
        this.modalContent.innerHTML = '';

        if (preventFocus) this.openedBy = null;
        super.hide();
      }

      show(opener) {
        opener.setAttribute('aria-disabled', true);
        opener.classList.add('loading');
        opener.querySelector('.loading__spinner').classList.remove('hidden');

        fetch(opener.getAttribute('data-product-url'))
          .then((response) => response.text())
          .then((responseText) => {
            const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
            const productElement = responseHTML.querySelector('product-info');

            this.preprocessHTML(productElement);
            HTMLUpdateUtility.setInnerHTML(this.modalContent, productElement.outerHTML);

            this.rescanPrintess();

            if (window.Shopify && Shopify.PaymentButton) {
              Shopify.PaymentButton.init();
            }
            if (window.ProductModel) window.ProductModel.loadShopifyXR();

            this.modalContent.querySelector('product-component')?.dispatchViewEvent?.();

            super.show(opener);
          })
          .finally(() => {
            opener.removeAttribute('aria-disabled');
            opener.classList.remove('loading');
            opener.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      // Printess scoping (2026-09-18): the Printess app-embed's initializer only ever scans the
      // page for `.printess-scope` buttons ONCE, at initial page load (confirmed live in devtools
      // — its `initialize()` entry point no-ops on any later call, and its claimed live
      // MutationObserver rescan doesn't actually pick up buttons added afterwards, confirmed via
      // a manual DOM-injection test). This modal's real add-to-cart button doesn't exist until
      // the fetch above injects it, well after that one-time scan already ran — so a
      // personalizable product opened via "Choose options" never got its Printess button without
      // this. Forces a fresh scan right after the modal's real content is injected, by briefly
      // clearing Printess's own one-shot guard (`editorButtons`/`slimUiButtons`) and calling its
      // public `initialize()` again with the same global `window.printessSettings` config the
      // app block's own bootstrap script used. `initialize()` re-`querySelectorAll`s the whole
      // page but skips any button already carrying `data-printess-attached`, so this is a no-op
      // everywhere except the newly-injected modal content — nothing already on the page gets
      // re-processed or duplicated. Restores (merges back) whatever buttons Printess had already
      // tracked before the rescan, since `initialize()` otherwise resets those tracking arrays to
      // only what it finds in this pass — other Printess features (e.g. its cart-page
      // integration) read those arrays and shouldn't lose earlier entries. Only runs at all when
      // the freshly-injected content actually contains a `.printess-scope` button, so a
      // non-personalizable product's quick-add never touches Printess.
      rescanPrintess() {
        if (typeof PrintessIntegrationLauncher === 'undefined' || !window.printessSettings) return;
        if (!this.modalContent.querySelector('.printess-scope')) return;

        const priorEditorButtons = PrintessIntegrationLauncher.editorButtons || [];
        const priorSlimUiButtons = PrintessIntegrationLauncher.slimUiButtons || [];

        PrintessIntegrationLauncher.editorButtons = null;
        PrintessIntegrationLauncher.slimUiButtons = null;

        Promise.resolve(PrintessIntegrationLauncher.initialize(window.printessSettings)).then(() => {
          PrintessIntegrationLauncher.editorButtons = priorEditorButtons.concat(PrintessIntegrationLauncher.editorButtons || []);
          PrintessIntegrationLauncher.slimUiButtons = priorSlimUiButtons.concat(PrintessIntegrationLauncher.slimUiButtons || []);
        });
      }

      // Undo the tracking rescanPrintess() adds, before this modal's content is wiped (2026-09-21)
      // — hide() used to just clear modalContent.innerHTML directly, leaving Printess's own
      // editorButtons/slimUiButtons arrays still holding an entry that points at the button this
      // modal is about to destroy. rescanPrintess() deliberately restores prior entries after every
      // initialize() call (see its own comment above) so nothing here ever got pruned — they just
      // built up forever, one more per modal open. The next open's rescan then re-processes those
      // now-detached stale entries along with the real one (confirmed live: a second "Choose size"
      // open throws a Printess "Cannot get templateName initialproperties" error for the FIRST
      // product while opening the SECOND, and the modal ends up with one extra "Add to basket"
      // button per previous open). Printess exposes printessRemoveButton() on each tracked entry
      // specifically to unregister it, so this calls that for any entry whose button lived inside
      // this modal (found via .contains() while the button is still connected, right before the
      // innerHTML wipe) instead of silently yanking the DOM out from under Printess. Entries for
      // buttons elsewhere on the page (e.g. a non-modal product page) are left untouched.
      cleanupPrintess() {
        if (typeof PrintessIntegrationLauncher === 'undefined') return;

        ['editorButtons', 'slimUiButtons'].forEach((key) => {
          const tracked = PrintessIntegrationLauncher[key];
          if (!Array.isArray(tracked)) return;

          PrintessIntegrationLauncher[key] = tracked.filter((entry) => {
            const button = entry.printessGetAddToBasketButton ? entry.printessGetAddToBasketButton() : null;
            const isInThisModal = button && this.modalContent.contains(button);
            if (isInThisModal && entry.printessRemoveButton) entry.printessRemoveButton();
            return !isInThisModal;
          });
        });
      }

      // Mirrors the card-preview navigation repositioning sections/main-product.liquid's own
      // script does on the real PDP (moves Printess's Front/Inside/Back/Preview thumbnails from
      // wherever it renders them by default to the bottom of the media gallery, styled as the SL
      // card rail) — but that script only ever loads on pages where the main-product section
      // itself is rendered, so it never runs for THIS modal's cloned <product-info> when quick-add
      // is opened from a collection page. Set up once here in the constructor (not per-open) so it
      // survives every open/close cycle without accumulating observers the way an un-cleaned-up
      // per-open observer would (see cleanupPrintess() above for the sibling bug this pattern
      // already caused once). Re-running the idempotent move logic on every mutation inside
      // modalContent means it doesn't matter exactly when Printess's async rescanPrintess()
      // finishes injecting the navigation — whenever it appears, the next mutation catches it.
      observePrintessNavigation() {
        let updateScheduled = false;

        const scheduleUpdate = () => {
          if (updateScheduled) return;

          updateScheduled = true;

          requestAnimationFrame(() => {
            updateScheduled = false;
            this.movePrintessNavigation();
          });
        };

        const observer = new MutationObserver(scheduleUpdate);

        observer.observe(this.modalContent, {
          childList: true,
          subtree: true
        });
      }

      movePrintessNavigation() {
        const productInfo = this.modalContent.querySelector('product-info.sl-active');
        if (!productInfo) return;

        const items = Array.from(productInfo.querySelectorAll('.slim-page-navigation-item'));
        if (!items.length) return;

        const navigation = items[0].parentElement;
        if (!navigation || navigation.querySelectorAll('.slim-page-navigation-item').length !== items.length) return;

        const mediaWrapper = productInfo.querySelector('.product__media-wrapper');
        if (!mediaWrapper) return;

        const stickyMediaGallery =
          mediaWrapper.querySelector('media-gallery.product__column-sticky') ||
          mediaWrapper.querySelector('media-gallery') ||
          mediaWrapper;

        navigation.classList.add('sl-bottom-page-navigation');

        let heading = productInfo.querySelector('.sl-page-navigation-heading');

        if (!heading) {
          heading = document.createElement('p');
          heading.className = 'sl-page-navigation-heading';
          heading.textContent = 'View card';
        }

        if (!heading.id) {
          heading.id = `sl-page-navigation-heading-${productInfo.dataset.section || 'quickadd'}`;
        }

        navigation.setAttribute('aria-labelledby', heading.id);

        if (
          navigation !== stickyMediaGallery &&
          !navigation.contains(stickyMediaGallery) &&
          (navigation.parentElement !== stickyMediaGallery || stickyMediaGallery.lastElementChild !== navigation)
        ) {
          stickyMediaGallery.appendChild(navigation);
        }

        if (navigation.previousElementSibling !== heading) {
          stickyMediaGallery.insertBefore(heading, navigation);
        }
      }

      preprocessHTML(productElement) {
        productElement.classList.forEach((classApplied) => {
          if (classApplied.startsWith('color-') || classApplied === 'gradient')
            this.modalContent.classList.add(classApplied);
        });
        this.preventDuplicatedIDs(productElement);
        this.removeDOMElements(productElement);
        this.removeGalleryListSemantic(productElement);
        this.updateImageSizes(productElement);
        this.preventVariantURLSwitching(productElement);
      }

      preventVariantURLSwitching(productElement) {
        productElement.setAttribute('data-update-url', 'false');
      }

      removeDOMElements(productElement) {
        const pickupAvailability = productElement.querySelector('pickup-availability');
        if (pickupAvailability) pickupAvailability.remove();

        const productModal = productElement.querySelector('product-modal');
        if (productModal) productModal.remove();

        const modalDialog = productElement.querySelectorAll('modal-dialog');
        if (modalDialog) modalDialog.forEach((modal) => modal.remove());
      }

      preventDuplicatedIDs(productElement) {
        const sectionId = productElement.dataset.section;

        const oldId = sectionId;
        const newId = `quickadd-${sectionId}`;
        productElement.innerHTML = productElement.innerHTML.replaceAll(oldId, newId);
        Array.from(productElement.attributes).forEach((attribute) => {
          if (attribute.value.includes(oldId)) {
            productElement.setAttribute(attribute.name, attribute.value.replace(oldId, newId));
          }
        });

        productElement.dataset.originalSection = sectionId;
      }

      removeGalleryListSemantic(productElement) {
        const galleryList = productElement.querySelector('[id^="Slider-Gallery"]');
        if (!galleryList) return;

        galleryList.setAttribute('role', 'presentation');
        galleryList.querySelectorAll('[id^="Slide-"]').forEach((li) => li.setAttribute('role', 'presentation'));
      }

      updateImageSizes(productElement) {
        const product = productElement.querySelector('.product');
        const desktopColumns = product?.classList.contains('product--columns');
        if (!desktopColumns) return;

        const mediaImages = product.querySelectorAll('.product__media img');
        if (!mediaImages.length) return;

        let mediaImageSizes =
          '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

        if (product.classList.contains('product--medium')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '605px');
        } else if (product.classList.contains('product--small')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '495px');
        }

        mediaImages.forEach((img) => img.setAttribute('sizes', mediaImageSizes));
      }
    }
  );
}
